import React, {useEffect, useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import * as tracking from '../../../tracking';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import DirectApi from '../../../directApi';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';
import {RadioButton} from '@/web-components';
import {
	settingsForNewProjectsAndTasks,
	settingsForExistingProjectsAndTasks,
	settingsForNewPhasesTasksAndTimeRegsOnExistingProjects,
} from './SettingsUtil';
import {FlexRow, Link, DeprecatedText as Text} from '@forecast-it/design-system';
import TooltipContainer from '../../shared/components/tooltips/tooltip_container';
import DataImportValidator from './DataImportValidator';
import {Team} from '../../../sentry/GetTeamFromPathname';
import ImportMode from './ImportMode';
import type {SettingsDataImporter_viewer$key as SettingsDataImporter_viewer} from './__generated__/SettingsDataImporter_viewer.graphql';
import {NuvoImportResult, Settings} from './Types';
import {SubmitDataImport} from './SubmitDataImport';
import {useIntl} from 'react-intl';
import {getLaunchDataImporter} from './loadIngestroVanilla';
import Util from '../../shared/util/util';
import {buildTimesheetLock, formatLockedDate} from './TimesheetLock';

/**
 * Ingestro (formerly Nuvo) v4 is loaded on demand from the CDN (see `loadIngestroVanilla.ts`) so the importer SDK
 * stays out of the main webpack bundle. `ingestroPublicTypes.ts` mirrors the SDK surface used here.
 */

export const DescriptionStyled = styled.div`
	overflow: auto;
	.description {
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 16px;
		padding-bottom: 16px;
	}
	.data-import-header {
		display: flex;
		flex-direction: column;
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 20px;
		padding-bottom: 0px;
	}
	.header-title {
		display: flex;
		color: #535353;
		font-size: 20px;
		font-weight: 500;
	}
	.sub-description {
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 15px;
	}
	.bold {
		font-weight: bold;
		padding-top: 15px;
		margin: 0px;
	}
	.button {
		display: flex;
		justify-content: flex-start;
		padding-left: 38px;
		margin-top: 20px;
		gap: 20px;
	}
	.my-nuvo-style1 {
		position: relative;
		max-height: 500px;
		max-width: 80vw;
		overflow: scroll;
	}
	.my-nuvo-style {
	}
`;

export const RateCardsStyle = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding-bottom: 10px;
`;

export const Section = styled.div`
	display: flex;
	max-width: 500px;
	flex-direction: column;
	flex-wrap: wrap;
	gap: 8px;
`;

export const SubSection = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	gap: 12px;
	padding-left: 8px;
	padding-top: 8px;
`;

const PAGE_NAME = 'Data Importer';

const TopHeader = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<div className="data-import-header">
			<div>
				<TooltipContainer
					infoText={[
						{
							unstyledList: [
								'Here you can import / migrate data to your account. For details see our guide ',
								<a target="_blank" href="https://support.forecast.app/hc/en-us">
									https://support.forecast.app
								</a>,
							],
							noBox: true,
						},
					]}
					edgeOffset={350}
				>
					<a className="header-title">Data Import</a>
				</TooltipContainer>
			</div>
			<Text size={2}>
				{intl.formatMessage({id: 'admin.data_importer.subtitle'})}&nbsp;
				<Link
					href={'https://support.forecast.app/hc/en-us/articles/12447804765713-Importing-data-into-Forecast'}
					target={'_blank'}
					rel="noopener noreferrer"
					size="2"
				>
					{intl.formatMessage({id: 'admin.data_importer.subtitle_link'})}
				</Link>
			</Text>
		</div>
	);
};

const ImportSelection = ({
	importMode,
	setImportMode,
	disabled = false,
}: {
	importMode: ImportMode;
	setImportMode: (mode: ImportMode) => void;
	disabled?: boolean;
}): React.JSX.Element => {
	return (
		<Section>
			<SubSection>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportNewProjects}
						onClick={() => setImportMode(ImportMode.ImportNewProjects)}
						disabled={disabled}
					/>
					<Text>
						Import{' '}
						<b>
							<u>new</u>
						</b>{' '}
						projects, tasks and time entries.
					</Text>
				</FlexRow>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportTimeRegs}
						onClick={() => setImportMode(ImportMode.ImportTimeRegs)}
						disabled={disabled}
					/>
					<Text>
						Import time entries into{' '}
						<b>
							<u>existing</u>
						</b>{' '}
						tasks or projects.
					</Text>
				</FlexRow>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportPhasesTasksTimeRegs}
						onClick={() => setImportMode(ImportMode.ImportPhasesTasksTimeRegs)}
						disabled={disabled}
					/>
					<Text>
						Import phases, tasks or time entries into{' '}
						<b>
							<u>existing</u>
						</b>{' '}
						projects.
					</Text>
				</FlexRow>
			</SubSection>
		</Section>
	);
};

type ResultTextProps = {success: boolean};
const ResultText = ({success}: ResultTextProps) => {
	if (success) {
		return (
			<div className="bold">
				<p>Data successfully uploaded and will now be added to your account.</p>
				<p>
					The actual time it takes before you can see the data can vary, but smaller data sets should not take more
					than a couple of minutes to process, whereas larger data sets can potentially take hours before everything
					is ready for you.
				</p>
				<p>For reference 1000 time entries will take approximately 5 minutes to import.</p>
			</div>
		);
	} else {
		return (
			<div>
				<p>The import failed. Please reach out to our support for further assistance.</p>
			</div>
		);
	}
};

const SettingsDataImporter_viewerFragment = graphql`
	fragment SettingsDataImporter_viewer on Viewer {
		email
		company {
			id
			allCompanyTaskIds
			allCompanyProjectIds
			lockedPeriodYear
			lockedPeriodMonth
			lockedPeriodDay
			allPersons(first: 10000) {
				edges {
					node {
						id
						email
						excludeFromCompanyLockedPeriod
					}
				}
			}
			clients(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
			rateCards(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
			roles(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
		}
	}
`;

type SettingsDataImporterProps = {
	viewer: SettingsDataImporter_viewer;
};

export const SettingsDataImporter = ({viewer: viewerRef}: SettingsDataImporterProps): React.JSX.Element => {
	const intl = useIntl();
	useTrackPage(PAGE_NAME);
	useEffect(() => {
		document.title = 'Data Importer - Account - Forecast';

		tracking.trackPage('settings-dataImporter');
	}, []);

	const [importMode, setImportMode] = useState(ImportMode.ImportNewProjects);

	const viewer = useFragment(SettingsDataImporter_viewerFragment, viewerRef);

	const [nuvoResult, setNuvoResult] = useState<NuvoImportResult | null>(null);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [migrationStatus, setMigrationStatus] = useState<boolean | null>(null);

	const existingData = useMemo(
		() => ({
			emails: (viewer.company?.allPersons?.edges?.map(e => e?.node?.email).filter(Boolean) as string[]) ?? [],
			clients: (viewer.company?.clients?.edges?.map(e => e?.node?.name).filter(Boolean) as string[]) ?? [],
			rateCards: (viewer.company?.rateCards?.edges?.map(e => e?.node?.name).filter(Boolean) as string[]) ?? [],
			projects: viewer.company?.allCompanyProjectIds?.filter(Boolean).map((id: number) => '' + id) ?? [],
			tasks: viewer.company?.allCompanyTaskIds?.filter(Boolean).map((id: number) => '' + id) ?? [],
		}),
		[viewer]
	);

	const timesheetLock = useMemo(
		() =>
			buildTimesheetLock(
				Util.hasTimeLocking(),
				{
					year: viewer.company?.lockedPeriodYear,
					month: viewer.company?.lockedPeriodMonth,
					day: viewer.company?.lockedPeriodDay,
				},
				viewer.company?.allPersons?.edges?.map(e => ({
					email: e?.node?.email,
					excludeFromCompanyLockedPeriod: e?.node?.excludeFromCompanyLockedPeriod,
				})) ?? []
			),
		[viewer]
	);

	const lockedPeriodMessage = timesheetLock
		? intl.formatMessage(
				{id: 'admin.data_importer.time_entry_in_locked_period'},
				{lockedDate: formatLockedDate(intl, timesheetLock.lockedDate)}
		  )
		: '';

	const confirmDataUpload = async () => {
		setSubmitLoading(true);
		const headers = new Headers();
		headers.append('Content-Type', 'application/json');
		const data = {
			method: 'POST',
			headers,
			credentials: 'include' as const,
			body: JSON.stringify(nuvoResult),
		};
		await fetch(DirectApi.graphqlServerEndpoint(`data_migration_upload`), data).then(response => {
			setSubmitLoading(false);
			setMigrationStatus(response.ok);
			if (!response.ok) {
				Sentry.captureException(new Error('Data Importer Failed'), {
					tags: {bugduty: true, team: Team.Team1},
				});
			}
		});
	};

	const cancelDataUpload = async () => {
		setNuvoResult(null);
		window.location.reload();
	};

	const {licenseKey, settings} = useMemo(() => {
		const clientOptions = existingData.clients.map(client => ({
			label: client,
			value: client,
			type: 'string' as const,
		}));
		const rateCardOptions = existingData.rateCards.map(rateCard => ({
			label: rateCard,
			value: rateCard,
			type: 'string' as const,
		}));

		let nextSettings: Settings;
		if (importMode === ImportMode.ImportNewProjects) {
			nextSettings = settingsForNewProjectsAndTasks(clientOptions, rateCardOptions);
		} else if (importMode === ImportMode.ImportTimeRegs) {
			nextSettings = settingsForExistingProjectsAndTasks();
		} else {
			nextSettings = settingsForNewPhasesTasksAndTimeRegsOnExistingProjects();
		}

		let nextLicenseKey = '';
		if (process.env.CIRCLE_BRANCH === 'production' && !viewer.email?.includes('@forecast.')) {
			nextLicenseKey = '+8GEeJk1ivE5xGFO6UiXIXwBOCkqqusXpqsvW3ilc78=';
			nextSettings.developerMode = false;
		} else {
			nextLicenseKey = 'KunfteTHBK0NElFNkgtu3ap0g/m4SJz6qtbLHXME1U8=';
		}

		return {licenseKey: nextLicenseKey, settings: nextSettings};
	}, [importMode, existingData, viewer.email]);

	const {columnHooks, onEntryChange, onEntryInit} = useMemo(() => {
		const validator = new DataImportValidator({
			importMode,
			projects: existingData.projects.map(id => 'P' + id),
			tasks: existingData.tasks.map(id => 'T' + id),
			emails: existingData.emails.map(e => e.toLowerCase()),
			companyIdBase64: viewer.company?.id,
			timesheetLock,
			lockedPeriodMessage,
		});

		if (importMode === ImportMode.ImportNewProjects) {
			validator.registerColumnHooksTaskEmails();
		} else if (importMode === ImportMode.ImportTimeRegs) {
			validator.registerValidationForExistingTasksAndProjects();
		} else if (importMode === ImportMode.ImportPhasesTasksTimeRegs) {
			validator.registerColumnHooksForExistingProjects();
			validator.registerColumnHooksTaskEmails();
		}

		validator.registerColumnHooksForTimeRegEmails();

		return {
			columnHooks: validator.getColumnHooks(),
			onEntryChange: validator.getOnEntryChange(),
			onEntryInit: validator.getOnEntryInit(),
		};
	}, [importMode, existingData, viewer.company?.id, timesheetLock, lockedPeriodMessage]);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const launch = await getLaunchDataImporter();
				if (cancelled) return;
				const container = document.getElementById('nuvo-container');
				if (!container) return;
				container.innerHTML = '';
				launch('#nuvo-container', {
					licenseKey,
					settings,
					columnHooks,
					onEntryChange,
					onEntryInit,
					onResults: (result, _errors, complete) => {
						setNuvoResult(result as NuvoImportResult);
						complete();
					},
					onCancel: () => void cancelDataUpload(),
				});
				const iframe = container.getElementsByTagName('iframe')[0];
				if (iframe) {
					if (window.innerHeight < 600) {
						iframe.style.height = '60vh';
					} else if (window.innerHeight < 900) {
						iframe.style.height = '70vh';
					} else {
						iframe.style.height = '80vh';
					}
				}
			} catch (e) {
				Sentry.captureException(e, {
					tags: {bugduty: true, team: Team.Team1},
				});
			}
		})();
		return () => {
			cancelled = true;
		};
		// Intentionally excludes cancelDataUpload (new function identity each render).
	}, [importMode, licenseKey, settings, columnHooks, onEntryChange, onEntryInit]);

	return (
		<DescriptionStyled>
			<TopHeader />
			<div className="description">
				<ImportSelection importMode={importMode} setImportMode={setImportMode} disabled={nuvoResult !== null} />
			</div>
			{nuvoResult === null ? (
				<div className="my-nuvo-style" id="nuvo-container" />
			) : migrationStatus === null ? (
				<SubmitDataImport
					importMode={importMode}
					newData={nuvoResult}
					existingData={existingData}
					onSubmit={confirmDataUpload}
					onCancel={cancelDataUpload}
					loading={submitLoading}
					timesheetLock={timesheetLock}
					companyIdBase64={viewer.company?.id}
				/>
			) : (
				<div className="sub-description">
					<ResultText success={migrationStatus} />
				</div>
			)}
		</DescriptionStyled>
	);
};

const settingsDataImporterQuery = graphql`
	query SettingsDataImporter_Query {
		viewer {
			actualPersonId
			component(name: "data_importer")
			...SettingsDataImporter_viewer
		}
	}
`;

export {settingsDataImporterQuery};
